import React from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {ResolveDispatchProps} from '../../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../helpers/reselectHelpers';
import {AttachmentModel, UploadedAttachmentModel} from '../../../../models/api/attachmentModel';
import {getCompany, getCompanyHasCsatSurveysFeature} from '../../../../store/data/company/companySelectors';
import {
  toggleListAutoFormattingAction,
  updateDefaultFontStyleAction,
} from '../../../../store/data/preferences/preferencesActions';
import {
  getDefaultFontStyle,
  getListAutoFormatting,
  getPasteAsText,
} from '../../../../store/data/preferences/preferencesSelectors';
import {getHasParagraphSpaceIndent} from '../../../../store/featureFlags/featureFlagsSelectors';
import {getEmojiValidationMap} from '../../../../store/stateless/emojiSelectors';
import {getI18n} from '../../../../store/stateless/internationalizationSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {AttachmentsUploader} from '../../../common/attachments/attachmentsUploader';
import {sanitizeDefaultFontStyle} from '../../../common/editor/style/defaultFontStyle';
import {SettingsComposer, SettingsComposerInheritableProps} from './settingsComposer';

/*
 * Props.
 */

interface ConnectedSettingsComposerContainerProps extends SettingsComposerInheritableProps {
  onAddAttachment: (attachment: UploadedAttachmentModel) => void;
  onRemoveAttachment: (cid: string) => void;
  attachments: ReadonlyArray<AttachmentModel>;
}

/*
 * Selectors.
 */

const getSanitizedDefaultFontStyle = createSelector(getDefaultFontStyle, sanitizeDefaultFontStyle);

/*
 * Component.
 */

interface SettingsComposerContainerComponentProps
  extends ConnectedSettingsComposerContainerProps,
    ResolveStateProps<typeof mapStateToProps>,
    ResolveDispatchProps<typeof mapDispatchToProps> {}

const SettingsComposerContainerComponent: React.FC<SettingsComposerContainerComponentProps> = (props) => {
  const {attachments, onAddAttachment, onRemoveAttachment} = props;

  return (
    <AttachmentsUploader
      company={props.company}
      attachments={attachments}
      onAttachmentUploaded={onAddAttachment}
    >
      {(attachmentsProps) => (
        <SettingsComposer {...props} {...attachmentsProps} onRemoveAttachmentClick={onRemoveAttachment} />
      )}
    </AttachmentsUploader>
  );
};

const mapStateToProps = makeMapStateToProps<ConnectedSettingsComposerContainerProps>()(() => ({
  defaultFontStyle: getSanitizedDefaultFontStyle,
  hasCsatSurveysFeature: getCompanyHasCsatSurveysFeature,
  hasParagraphSpaceIndent: getHasParagraphSpaceIndent,
  i18n: getI18n,
  emojiValidationMap: getEmojiValidationMap,
  hasListAutoFormatting: getListAutoFormatting,
  shouldPasteAsText: getPasteAsText,
  company: getCompany,
}));

const mapDispatchToProps = makeMapDispatchToProps<ConnectedSettingsComposerContainerProps>()(() => ({
  updateDefaultFontStyle: updateDefaultFontStyleAction,
  navigateToLinkAction,
  toggleListAutoFormatting: toggleListAutoFormattingAction,
}));

export const SettingsComposerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  (s, d, o) => ({...s, ...d, ...o}),
  {forwardRef: true},
)(SettingsComposerContainerComponent);
